/**
 * tracks-context.js
 */

import React from 'react';
import { useSnackbar } from 'notistack';
import cloneDeep from 'lodash.clonedeep';
import * as Sentry from "@sentry/react";
import { renderForMobileContext } from 'utils/app-context';
import useRecordStore from 'contexts/record-store/record-store';
import { defaultPointFilter } from './map/point-filtering';
import { TRACK_STORAGE_KEY, calculateCustomPointProperties, processPoints } from './tracking-common';
import TrackingPersistence from './tracking-persistence';


export const TrackContext = React.createContext();

// trackList objects are normal track objects except with additional props:
// - pointsRaw: raw list of unprocessed points
// - pointsProcessed: list of processed points that include additional calculated fields
// these props are removed in public tracks obj
export const TrackProvider = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const recordStore = useRecordStore();
  const [state, setState] = React.useState({
    persistenceManager: null,
  });
  const persistenceManager = React.useRef();
  const [trackList, setTrackList] = React.useState([]);
  const tracks = React.useMemo(() => 
    trackList.map(({ pointsRaw, pointsProcessed, ...track }) => ({ 
      ...track,
      collectedPointCount: pointsRaw.length,
    })), 
    [trackList]); 



  // -----------------------------------------------------------------------------------------
  // private 
  // -----------------------------------------------------------------------------------------


  const processNewTrack = (track, doFilterPoints = true) => {
    const processedPoints = processPoints(track.points);
    return {
      ...track,
      pointsRaw: track.points,
      pointsProcessed: processedPoints,
      points: doFilterPoints ? processPoints(defaultPointFilter(processedPoints)) : processedPoints
    };
  }


  // -----------------------------------------------------------------------------------------
  // public 
  // -----------------------------------------------------------------------------------------


  // set tracks. 
  // use this when setting tracks or track points. otherwise use: createTrack, updateTrack, addPoint
  const setTracks = React.useCallback((tracks, doFilterPoints = true) => {
    setTrackList(p => {
      // create a new uuid for each marker being loaded for the first time, but don't
      // overwrite uuids for markers that have been previously loaded.
      // TODO: this feels problematic: UUIDs weren't meant to be required for all track elements for the system to work,
      // but rather to provide a unique id when there isn't one yet. having to assign a UUID whenever we set 
      // tracks indicates that markers have a UUID dependence issue. To simplify this for now, we just
      // use the existing marker.id property for marker.uuid since, in this context, a track's markers are 
      // always already persisted.
      return tracks.map(track => ({
        ...processNewTrack(track, doFilterPoints),
        markers: track.markers.map(marker => ({
          ...marker,
          uuid: marker.id
        }))
      }));
    });
  }, []);


  // adds track to track list and persistence queue
  const createTrack = React.useCallback((track) => {
    persistenceManager.current.queueTrack(track);
    setTrackList(p => [ ...p, processNewTrack(track) ]);
  }, []);


  // updates specified track by adding new props.
  // props can be a partial object or a function, taking the existing track as an argument and returning a new partial object.
  // NOTE: do not use this to update track points.
  const updateTrack = React.useCallback((id, props) => {
    if (props.points) {
      throw new Error(`Invalid use of updateTrack: props argument must not contain 'points' property.`);
    }

    setTrackList(p => p.map(track => track.id === id
      ? typeof props === 'function' ? { ...track, ...props(track) } : { ...track, ...props }
      : track
    ));
  }, []);


  // adds point to active track. 
  // checks for and prevents duplicating the latest point.
  const addPoint = React.useCallback((point) => {
    setTrackList(p => {
      const activeTrack = p.find(t => t.isActive);
      if (!activeTrack) {
        return p;
      }

      const firstPoint = activeTrack.pointsProcessed[0];
      const prevPoint = activeTrack.pointsProcessed[activeTrack.pointsProcessed.length - 1];
      const processedPoint = calculateCustomPointProperties(firstPoint, prevPoint, point);

      // prevent dupe
      if (prevPoint.latitude === point.latitude && prevPoint.longitude === point.longitude) {
        return p;
      }
      
      persistenceManager.current.queuePoint(activeTrack.id, point);

      return p.map(track => {
        if (track.isActive) {
          const processedPoints = [ ...track.pointsProcessed, processedPoint ];
          return {
            ...track,
            pointsRaw: [ ...track.pointsRaw, point ],
            pointsProcessed: processedPoints,
            points: defaultPointFilter(processedPoints)
          };
        } else {
          return track;
        }
      });
    });
  }, []);


  // saves track list and persistence state to local storage. called by live tracking context.
  const persistState = React.useCallback(() => {
    const storageObj = {
      trackList: cloneDeep(trackList),
      persistenceManager: {
        trackCreationQueue: cloneDeep(persistenceManager.current.trackCreationQueue),
        partPersistenceQueue: cloneDeep(persistenceManager.current.partPersistenceQueue)
      }
    }

    // delete duplicate/unnecessary point data to save space
    storageObj.trackList.forEach(track => {
      if (track.leadTrack) {
        delete track.leadTrack.points;
      }
    });
    storageObj.persistenceManager.partPersistenceQueue.forEach(pqTrack => {
      // only delete points if the tracks list has same track
      if (storageObj.trackList.find(t => t.id === pqTrack.id)) {
        delete pqTrack.points;
      }
      // delete leadTrack points since they aren't used
      if (pqTrack?.leadTrack?.points) {
        delete pqTrack.leadTrack.points;
      }
    });

    try {
      localStorage.setItem(TRACK_STORAGE_KEY, JSON.stringify(storageObj));
    } catch {}
  }, [trackList]);


  // restores state from local storage if exists.
  // called by live tracking context to ensure it only happens at top level.
  const restoreState = React.useCallback((resetTracking) => {
    if (renderForMobileContext()) {
      const storageObjRaw = localStorage.getItem(TRACK_STORAGE_KEY);
      const storageObj = storageObjRaw ? JSON.parse(storageObjRaw) : null;
      if (storageObj && persistenceManager.current) {
        // basic sanity checks
        if (storageObj.trackList.length === 0 || !storageObj.trackList.find(track => track.isActive)) {
          // limit logged points to prevent over-size data
          storageObj.trackList.forEach(track => {
            track.points = track.points.slice(0, 10);
          });
          storageObj.persistenceManager.partPersistenceQueue.forEach(track => {
            if (track.points) {
              track.points = track.points.slice(0, 10);
            }
          });

          Sentry.captureMessage(
            'Track context restore failed sanity check', 
            { extra: { [TRACK_STORAGE_KEY]: JSON.stringify(storageObj) }});
          resetTracking();
          return;
        }

        storageObj.persistenceManager.partPersistenceQueue.forEach(pqTrack => {
          if (!pqTrack.points) {
            // get points from tracks list
            const siblingTrack = storageObj.trackList.find(t => t.id === pqTrack.id);
            if (!siblingTrack) {
              console.error('unable to find sibling track', pqTrack, storageObj.trackList);
              pqTrack.points = [];
            } else {
              const points = cloneDeep(siblingTrack.pointsRaw);
              pqTrack.points = points;
            }
          }
        });
        persistenceManager.current.trackCreationQueue = storageObj.persistenceManager.trackCreationQueue;
        persistenceManager.current.partPersistenceQueue = storageObj.persistenceManager.partPersistenceQueue;
        setTrackList(storageObj.trackList);
        return;
      }
    }
  }, []);



  // -----------------------------------------------------------------------------------------
  // effects
  // -----------------------------------------------------------------------------------------


  // init/update - persistence class
  React.useEffect(() => {
    // init persistence class
    if (!persistenceManager.current) {
      persistenceManager.current = new TrackingPersistence(enqueueSnackbar, recordStore);
      persistenceManager.current.updateTrack = updateTrack;
      setState(p => ({ 
        ...p, 
        persistenceManager: persistenceManager.current,
      }));
    }
  }, [enqueueSnackbar, recordStore, updateTrack]);


  // persistence cleanup here in effect with no params ensures it only happens once at end
  // instead of whenever params change.
  React.useEffect(() => {
    return () => {
      if (persistenceManager.current) {
        persistenceManager.current.destroy();
      }
    };
  }, []);




  // -----------------------------------------------------------------------------------------
  // final state value
  // -----------------------------------------------------------------------------------------


  const value = React.useMemo(() => ({
    ...state,
    tracks,
    setTracks,
    createTrack,
    updateTrack,
    addPoint,
    persistState,
    restoreState,
  }), [
    state,
    tracks,
    setTracks,
    createTrack,
    updateTrack,
    addPoint,
    persistState,
    restoreState,
  ]);


  return (<>
    <TrackContext.Provider 
      value={value}
      children={props.children}
    />
  </>);
}